import type { JSX } from 'preact';
import './MentionMarkdown.css';

/**
 * Renders an annotation body with `@[displayName](uuid)` mention markdown
 * into a mix of text nodes + clickable mention pills. No HTML rendering,
 * no markdown parsing beyond the mention syntax — the body is untrusted
 * user input, so we render as text nodes and only wrap the mention runs.
 */
export function MentionMarkdown({ body }: { body: string }) {
  const parts = splitMentions(body);
  return (
    <span class="hv-mention-md">
      {parts.map((p, i) => p.kind === 'mention' ? (
        <span key={i} class="hv-mention-md__pill" title={`Erwähnung: ${p.userId}`}>@{p.name}</span>
      ) : (
        <span key={i}>{p.text}</span>
      ))}
    </span>
  );
}

type Part =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; name: string; userId: string };

const MENTION_RE = /@\[([^\]]+)\]\(([0-9a-fA-F-]{36})\)/g;

function splitMentions(body: string): Part[] {
  const out: Part[] = [];
  let last = 0;
  MENTION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MENTION_RE.exec(body)) !== null) {
    if (m.index > last) {
      out.push({ kind: 'text', text: body.slice(last, m.index) });
    }
    out.push({ kind: 'mention', name: m[1], userId: m[2] });
    last = m.index + m[0].length;
  }
  if (last < body.length) {
    out.push({ kind: 'text', text: body.slice(last) });
  }
  return out;
}

// Typed re-export helper: some callers want to know if a body has any
// mentions before deciding on a compact vs. expanded render.
export function bodyHasMentions(body: string): boolean {
  MENTION_RE.lastIndex = 0;
  return MENTION_RE.test(body);
}

// Silence TS unused-import if a JSX pragma changes upstream.
export type _MMJSX = JSX.Element;
